import { fetchGETRequest, fetchPOSTRequest } from "./services";

const API_V2_BILLS = "v2/Bills";

export interface BillLineItemV2 {
  id: string;
  billId: string;
  meterId: string;
  meterNumber: string;
  siteId: string;
  siteName: string;
  description: string;
  consumption: number;
  rate: number;
  amount: number;
  tariffSlabId: string;
  tariffSlabName: string;
  createdAt: string;
}

export interface BillV2 {
  id: string;
  billNumber: string;
  customerId: string;
  customerName: string;
  contractId: string;
  contractNumber: string;
  siteId: string;
  siteName: string;
  billingType: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  totalConsumption: number;
  totalAmount: number;
  taxAmount: number;
  adjustmentAmount: number;
  previousBalance: number;
  netAmount: number;
  previousReading?: number;
  currentReading?: number;
  dueDate: string;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  tariffId: string | null;
  tariffName: string | null;
  tariffVersion: number | null;
  createdAt: string;
  updatedAt: string;
  lineItems: BillLineItemV2[];
  lastReadTimestamp: string | null;
}

export interface PaymentV2 {
  id: string;
  paymentNumber: string;
  customerId: string;
  customerName: string;
  contractId: string;
  contractNumber: string;
  billId: string;
  billNumber: string;
  amount: number;
  paymentMethod: string | null;
  paymentDate: string;
  paymentReference: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentListV2Response {
  success: boolean;
  data: PaymentV2[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaymentDetailsV2Response {
  success: boolean;
  message: string | null;
  data: PaymentV2;
  errors?: any;
}

export interface CreatePaymentV2Request {
  customerId: string;
  contractId: string;
  billId: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  paymentReference: string;
}

export interface BillListV2Response {
  success: boolean;
  data: BillV2[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  errors?: any;
}

export interface BillDetailsV2Response {
  success: boolean;
  message: string | null;
  data: BillV2;
  errors?: any;
}

export interface BillGenerateV2Request {
  customerId: string;
  contractId: string;
  siteId: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  dueDate: string;
}

export interface BillApproveV2Request {
  approvedBy: string;
}

export const getBillsV2 = async (params: {
  customerId?: string;
  contractId?: string;
  siteId?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  billNumber?: string;
  page?: number;
  pageSize?: number;
}) => {
  let query = "";
  const queryParts = [];
  if (params.customerId) queryParts.push(`CustomerId=${params.customerId}`);
  if (params.contractId) queryParts.push(`ContractId=${params.contractId}`);
  if (params.siteId) queryParts.push(`SiteId=${params.siteId}`);
  if (params.status) queryParts.push(`Status=${params.status}`);
  if (params.fromDate) queryParts.push(`FromDate=${params.fromDate}`);
  if (params.toDate) queryParts.push(`ToDate=${params.toDate}`);
  if (params.billNumber) queryParts.push(`BillNumber=${params.billNumber}`);
  if (params.page !== undefined) queryParts.push(`Page=${params.page + 1}`);
  if (params.pageSize !== undefined)
    queryParts.push(`PageSize=${params.pageSize}`);

  if (queryParts.length > 0) {
    query = `?${queryParts.join("&")}`;
  }

  return await fetchGETRequest<BillListV2Response>(`${API_V2_BILLS}${query}`);
};

export const getBillDetailsV2 = async (id: string) => {
  return await fetchGETRequest<BillDetailsV2Response>(`${API_V2_BILLS}/${id}`);
};

export const generateBillV2 = async (body: BillGenerateV2Request) => {
  return await fetchPOSTRequest<BillDetailsV2Response>(
    `${API_V2_BILLS}/generate`,
    body,
  );
};

export const approveBillV2 = async (id: string, body: BillApproveV2Request) => {
  return await fetchPOSTRequest<BillDetailsV2Response>(
    `${API_V2_BILLS}/${id}/approve`,
    body,
  );
};

export const cancelBillV2 = async (id: string) => {
  return await fetchPOSTRequest<BillDetailsV2Response>(
    `${API_V2_BILLS}/${id}/cancel`,
    {},
  );
};

const API_V2_PAYMENTS = "v2/Payments";

export const getPaymentsV2 = async (params: {
  customerId?: string;
  contractId?: string;
  billId?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}) => {
  let query = "";
  const queryParts = [];
  if (params.customerId) queryParts.push(`CustomerId=${params.customerId}`);
  if (params.contractId) queryParts.push(`ContractId=${params.contractId}`);
  if (params.billId) queryParts.push(`BillId=${params.billId}`);
  if (params.status) queryParts.push(`Status=${params.status}`);
  if (params.fromDate) queryParts.push(`FromDate=${params.fromDate}`);
  if (params.toDate) queryParts.push(`ToDate=${params.toDate}`);
  if (params.page !== undefined) queryParts.push(`Page=${params.page + 1}`);
  if (params.pageSize !== undefined)
    queryParts.push(`PageSize=${params.pageSize}`);

  if (queryParts.length > 0) {
    query = `?${queryParts.join("&")}`;
  }

  const res = await fetchGETRequest<PaymentListV2Response>(
    `${API_V2_PAYMENTS}${query}`,
  );

  // Fallback: If backend doesn't support BillId filter, filter locally
  if (res && res.success && params.billId && res.data) {
    res.data = res.data.filter((p) => p.billId === params.billId);
    res.totalCount = res.data.length;
  }

  return res;
};

export const getPaymentDetailsV2 = async (id: string) => {
  return await fetchGETRequest<PaymentDetailsV2Response>(
    `${API_V2_PAYMENTS}/${id}`,
  );
};

export const createPaymentV2 = async (body: CreatePaymentV2Request) => {
  // Use PascalCase as requested/observed in project patterns
  const pascalBody = {
    CustomerId: body.customerId,
    ContractId: body.contractId,
    BillId: body.billId,
    Amount: body.amount,
    PaymentMethod: body.paymentMethod,
    PaymentDate: body.paymentDate,
    PaymentReference: body.paymentReference,
  };
  return await fetchPOSTRequest<PaymentDetailsV2Response>(
    API_V2_PAYMENTS,
    pascalBody,
  );
};

export const refundPaymentV2 = async (id: string, reason: string) => {
  return await fetchPOSTRequest<PaymentDetailsV2Response>(
    `${API_V2_PAYMENTS}/${id}/refund`,
    { reason },
  );
};
